import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import debounce from '@core/lodash/debounce';
import throttle from '@core/lodash/throttle';
import { useStore } from '@core/widgets/store';
import { paramsSelector } from '@core/widgets/store/selectors';
import { useTrackEmbedScrollEndVideos } from '@core/widgets/tracking/useTrackEmbedScrollEndVideos';
import { useTrackEmbedScrollVideos } from '@core/widgets/tracking/useTrackEmbedScrollVideos';
export const THROTTLE_ONSCROLL_TIME = 1000;
export const DEBOUNCE_ONSCROLLEND_TIME = 500;
/**
 * Hook used for dispatching tracking events for in-viewport Thumbnails in Grid
 * while grid (window) is scrolling.
 */
export const useGridScroll = (scrollRef) => {
    const params = useStore(paramsSelector);
    const trackEmbedScrollVideos = useTrackEmbedScrollVideos();
    const trackEmbedScrollEndVideos = useTrackEmbedScrollEndVideos();
    const trackEmbedScrollVideos_throttled = useMemo(() => throttle(trackEmbedScrollVideos, THROTTLE_ONSCROLL_TIME, {
        leading: false,
    }), [trackEmbedScrollVideos]);
    // Cancel throttled function on unmount
    useEffect(() => trackEmbedScrollVideos_throttled.cancel, [trackEmbedScrollVideos_throttled]);
    const trackEmbedScrollEndVideos_debounced = useMemo(() => debounce(trackEmbedScrollEndVideos, DEBOUNCE_ONSCROLLEND_TIME), [trackEmbedScrollEndVideos]);
    // Cancel debounced function on unmount
    useEffect(() => trackEmbedScrollEndVideos_debounced.cancel, [trackEmbedScrollEndVideos_debounced]);
    const onGridScroll = useCallback(() => {
        trackEmbedScrollVideos_throttled();
        trackEmbedScrollEndVideos_debounced();
    }, [trackEmbedScrollVideos_throttled, trackEmbedScrollEndVideos_debounced]);
    useLayoutEffect(() => {
        const grid = scrollRef.current;
        if (grid) {
            if (params.mode === 'row' || params.mode === 'pinned') {
                grid.addEventListener('scroll', onGridScroll, { passive: true });
                return () => grid.removeEventListener('scroll', onGridScroll);
            }
            else {
                window.addEventListener('scroll', onGridScroll, { passive: true });
                return () => window.removeEventListener('scroll', onGridScroll);
            }
        }
    }, [scrollRef, params.mode, onGridScroll]);
};
